/**
 * StudioHome — premium creator-focused landing page.
 *
 * Hero + quick action cards + personal workspace tiles. Visually rich, mobile
 * first, and designed to be screenshot-worthy. Reads usage from
 * /api/studio/me — NO new aggregation engine.
 */
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import StudioLayout from '../components/StudioLayout';
import {
  ChefHat, Calculator, DollarSign, Calendar, BookOpen, Sparkles,
  ArrowRight, TrendingUp, ChevronRight, Package,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function HeroCTA({ to, label, Icon, primary }) {
  return (
    <Link to={to}
      data-testid={`studio-hero-cta-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`inline-flex items-center gap-2 h-11 px-5 rounded-full font-semibold text-sm shadow-md hover:shadow-xl transition-all ${
        primary
          ? 'bg-gradient-to-r from-[#E8643E] to-[#D44E2A] hover:from-[#D44E2A] hover:to-[#B73C1B] text-white'
          : 'bg-white/70 backdrop-blur-sm border border-[#F4ECE0] text-[#3A1F12] hover:bg-white'
      }`}>
      <Icon className="w-4 h-4" />
      {label}
      <ArrowRight className="w-3.5 h-3.5 opacity-70" />
    </Link>
  );
}

function QuickActionCard({ to, title, description, Icon, accent, testId }) {
  return (
    <Link to={to}
      data-testid={testId}
      className="group relative overflow-hidden rounded-2xl bg-white/80 backdrop-blur-sm border border-[#F4ECE0] hover:border-[#E8643E]/40 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all p-5 flex flex-col">
      <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-30 blur-2xl group-hover:opacity-60 transition-opacity ${accent}`} />
      <div className="relative">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-md mb-3 ${accent}`}>
          <Icon className="w-5 h-5 text-white" strokeWidth={2} />
        </div>
        <h3 className="text-base font-bold text-[#3A1F12] mb-1.5 group-hover:text-[#7A2A12]">{title}</h3>
        <p className="text-xs text-[#7A5A4B] leading-relaxed">{description}</p>
        <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-[#7A2A12] opacity-0 group-hover:opacity-100 transition-opacity">
          Open <ChevronRight className="w-3 h-3" />
        </div>
      </div>
    </Link>
  );
}

export default function StudioHome() {
  const { getHeaders, user } = useAuth();
  const [me, setMe] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/studio/me`, { headers: getHeaders() })
      .then(r => setMe(r.data))
      .catch(() => {
        // Wrong role / no token → bounce to studio login
        navigate('/studio/login', { replace: true });
      });
  }, [getHeaders, navigate]);

  if (!me) return <StudioLayout><div className="text-center py-20 text-sm text-[#7A2A12]">Loading your studio…</div></StudioLayout>;

  const usage = me.usage || {};
  const ent = me.entitlements || {};

  return (
    <StudioLayout>
      <div data-testid="studio-home-page" className="space-y-8">
        {/* Hero */}
        <section className="relative overflow-hidden rounded-3xl border border-[#F4ECE0] bg-gradient-to-br from-[#FFE7CD] via-[#FFFBF5] to-[#FCE7D6] p-6 sm:p-10 shadow-lg"
          data-testid="studio-hero">
          <div className="absolute right-0 top-0 w-64 h-64 rounded-full opacity-30 blur-3xl bg-[#E8643E]" />
          <div className="absolute left-1/3 bottom-0 w-48 h-48 rounded-full opacity-20 blur-3xl bg-[#F4B860]" />
          <div className="relative max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 backdrop-blur-sm border border-[#F4ECE0] mb-4">
              <Sparkles className="w-3 h-3 text-[#E8643E]" />
              <span className="text-[10px] uppercase tracking-wider text-[#7A2A12] font-semibold">
                Welcome back, {user?.name || 'Chef'}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#3A1F12] tracking-tight leading-[1.05]">
              Build smarter recipes.<br />
              <span className="bg-gradient-to-r from-[#E8643E] to-[#D44E2A] bg-clip-text text-transparent">Cost better.</span>{' '}
              Operate profitably.
            </h1>
            <p className="text-sm sm:text-base text-[#5C4A3D] mt-3 max-w-xl leading-relaxed">
              Your private creator workspace for ingredient costing, recipe scaling, smart pricing, and production planning — purpose-built for chefs &amp; restaurateurs.
            </p>
            <div className="flex flex-wrap items-center gap-2.5 mt-5">
              <HeroCTA to="/studio/recipes" label="Create Recipe" Icon={ChefHat} primary />
              <HeroCTA to="/studio/pricing" label="Start Costing" Icon={Calculator} />
              <HeroCTA to="/studio/library" label="Recipe Library" Icon={BookOpen} />
            </div>
          </div>
        </section>

        {/* Quick action cards */}
        <section data-testid="studio-quick-actions">
          <div className="flex items-end justify-between mb-3">
            <div>
              <h2 className="text-lg font-bold text-[#3A1F12]">Quick actions</h2>
              <p className="text-xs text-[#7A5A4B]">Jump straight into the tool you need.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <QuickActionCard to="/studio/ingredients" testId="studio-action-ingredients"
              title="Ingredients" Icon={Package}
              description="Bulk import from Excel, manage units & purchase prices — the foundation for every recipe."
              accent="bg-gradient-to-br from-[#A88B70] to-[#7A6650]" />
            <QuickActionCard to="/studio/recipes" testId="studio-action-recipes"
              title="Recipe Costing" Icon={ChefHat}
              description="Build recipes with multi-level sub-recipes, automatic cost roll-up, and portion conversion."
              accent="bg-gradient-to-br from-[#E8643E] to-[#D44E2A]" />
            <QuickActionCard to="/studio/pricing" testId="studio-action-pricing"
              title="Pricing Studio" Icon={DollarSign}
              description="Recipe-level Theo %, suggested menu price, Swiggy/Zomato uplift — zero spreadsheets."
              accent="bg-gradient-to-br from-[#F4B860] to-[#E8943C]" />
            <QuickActionCard to="/studio/production" testId="studio-action-production"
              title="Production Planner" Icon={Calendar}
              description="Plan a service, calculate ingredient picklists, and prepare-now / activate-next flows."
              accent="bg-gradient-to-br from-[#7A9D6E] to-[#5C7E52]" />
          </div>
        </section>

        {/* Personal workspace tiles */}
        <section data-testid="studio-workspace-tiles">
          <div className="flex items-end justify-between mb-3">
            <div>
              <h2 className="text-lg font-bold text-[#3A1F12]">Your workspace</h2>
              <p className="text-xs text-[#7A5A4B]">Snapshot of what you've built so far.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-2xl bg-white/80 backdrop-blur-sm border border-[#F4ECE0] p-4 shadow-sm"
              data-testid="studio-stat-recipes">
              <p className="text-[10px] uppercase tracking-wider text-[#A0846B] font-semibold">Recipes</p>
              <p className="text-3xl font-extrabold text-[#3A1F12] tabular-nums mt-1">{usage.recipes || 0}</p>
              <p className="text-[10px] text-[#7A5A4B] mt-0.5">of {ent.max_recipes || '∞'} allowed</p>
            </div>
            <div className="rounded-2xl bg-white/80 backdrop-blur-sm border border-[#F4ECE0] p-4 shadow-sm"
              data-testid="studio-stat-ingredients">
              <p className="text-[10px] uppercase tracking-wider text-[#A0846B] font-semibold">Ingredients</p>
              <p className="text-3xl font-extrabold text-[#3A1F12] tabular-nums mt-1">{usage.ingredients || 0}</p>
              <p className="text-[10px] text-[#7A5A4B] mt-0.5">of {ent.max_ingredients || '∞'} allowed</p>
            </div>
            <div className="rounded-2xl bg-white/80 backdrop-blur-sm border border-[#F4ECE0] p-4 shadow-sm">
              <p className="text-[10px] uppercase tracking-wider text-[#A0846B] font-semibold">Modules unlocked</p>
              <p className="text-3xl font-extrabold text-[#3A1F12] tabular-nums mt-1">{(ent.modules || []).length}</p>
              <p className="text-[10px] text-[#7A5A4B] mt-0.5">{(ent.modules || []).join(' · ')}</p>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-[#E8643E] to-[#D44E2A] text-white p-4 shadow-md">
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingUp className="w-3 h-3" />
                <p className="text-[10px] uppercase tracking-wider font-semibold opacity-90">Pro tip</p>
              </div>
              <p className="text-[12px] font-medium leading-snug">
                Add the <span className="font-bold">portions yield</span> to a weight recipe and Studio auto-calculates cost-per-plate.
              </p>
            </div>
          </div>
        </section>

        {/* Bottom CTA strip */}
        <section className="relative rounded-3xl overflow-hidden p-6 sm:p-8 border border-[#F4ECE0] shadow-md"
          style={{
            background: 'linear-gradient(135deg, #3A1F12 0%, #5C2E1A 100%)',
          }}>
          <div className="absolute right-0 top-0 w-64 h-64 rounded-full opacity-20 blur-3xl bg-[#E8643E]" />
          <div className="relative max-w-xl">
            <h3 className="text-lg sm:text-xl font-bold text-white mb-1">Ready to share your craft?</h3>
            <p className="text-sm text-[#FFE7CD]/80 max-w-md">
              Public sharing &amp; community recipes are rolling out next. Lock your workspace today and be ready when it lights up.
            </p>
          </div>
        </section>
      </div>
    </StudioLayout>
  );
}
